import { FormLabel, Grid, Typography } from "@mui/material";
import {
  CheckboxGroupInput,
  DateInput,
  PaginationPayload,
  required,
  SaveButton,
  TextInput,
  useGetList,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import { z } from "zod";
import { useAutovioContext } from "../../hooks/useAutovioContext";
import { Column } from "../../misc/Column";
import { DateTime } from "luxon";
import { CreateStudentDtoSchema } from "../../providers/studentsProvider";
import { Bundle } from "../../providers/catalogProvider";
import { SelectInput } from "../../inputs/SelectInput";
import { YesNoInput } from "../../inputs/YesNoInput";
import { DrivingLicenseClassEnum } from "../../model/DrivingLicenseClass";
import { Branch } from "../../providers/branchesProvider";
import { RadioGroupInput } from "../../inputs/RadioGroupInput";

export type CreateStudentFormParams = z.infer<typeof CreateStudentDtoSchema>;

export interface CreateStudentFormValues {
  assistedDrivingBF17: boolean;
  branchId: string;
  bundleId: string;
  contactData: {
    email: string;
    phone: string;
    city: string;
    postalCode: string;
    street: string;
    firstName: string;
    lastName: string;
  };
  dateOfBirth: string;
  guardian?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  needsGlasses: boolean;
  previousDrivingLicenseClasses: string[];
  placeOfBirth: string;
}

export function CreateStudentForm() {
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextInput label="Vorname" source="contactData.firstName" validate={required()} autoFocus fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput label="Nachname" source="contactData.lastName" validate={required()} fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput
            label="E-Mail *"
            source="contactData.email"
            validate={(email) => {
              if (!email?.trim()) {
                return "Bitte gib deine E-Mail-Adresse ein.";
              }
              if (!_isEmail(email.trim())) {
                return "Bitte gib eine gültige E-Mail-Adresse ein.";
              }
              if (email.endsWith("@mustermann.de")) {
                return "Bitte gib eine andere E-Mail-Adresse ein.";
              }
              return null;
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput label="Telefonnummer" source="contactData.phone" validate={required()} fullWidth />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextInput
            source="contactData.street"
            label="Straße & Hausnummer *"
            validate={(value) => {
              if (!value) {
                return "Bitte gib deine Straße und Hausnummer ein.";
              }
              const [street, streetNumber] = _splitStreet(value);
              if (!street) {
                return "Bitte gib deine Straße ein.";
              }
              if (!streetNumber) {
                return "Bitte gib deine Hausnummer ein.";
              }
              return null;
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput
            className="plz-input"
            source="contactData.postalCode"
            label="PLZ"
            validate={required()}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput source="contactData.city" label="Stadt" validate={required()} fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInput label="Geburtsort" source="placeOfBirth" validate={required()} fullWidth />
        </Grid>
        <Grid item xs={12} md={6}>
          <DateInput
            label="Geburtsdatum"
            source="dateOfBirth"
            validate={required()}
            fullWidth
            defaultValue={DateTime.now().minus({ years: 18 }).toISODate()}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <BundlesSelectInput />
        </Grid>
        <Grid item xs={12} md={6}>
          <BranchSelectInput />
        </Grid>
        <Grid item xs={6} md={3}>
          <RadioGroupInput
            source="paymentSelection"
            label="Zahlungsmodus *"
            options={[
              ["prepaid", "Nur Vorauszahlung"],
              ["invoice_payment", "Per Rechnung"],
            ]}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Column>
            <FormLabel>Sehhilfe benötigt?</FormLabel>
            <YesNoInput source="needsGlasses" />
          </Column>
        </Grid>
        <Bf17GridInput />
        <Grid item xs={12} md={12}>
          <CheckboxGroupInput
            label="Vorbesitz Führerscheinklassen"
            source="previousDrivingLicenseClasses"
            choices={DrivingLicenseClassEnum.options.map((it) => ({ id: it, name: it }))}
          />
        </Grid>
        <LegalGuardianInputs />
      </Grid>
      <SaveButton icon={<></>} />
    </>
  );
}

function BundlesSelectInput() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const { data: bundles = [] } = useGetList<Bundle>("bundles", {
    filter: { drivingSchoolId },
    sort: { field: "name", order: "ASC" },
    pagination: {} as any as PaginationPayload,
  });

  return (
    <SelectInput
      source="bundleId"
      label="Paket *"
      options={bundles.map((it) => [it.id, it.name])}
      validate={(value) => (value ? true : `Bitte wähle den Paket aus.`)}
      size="small"
      sx={{ width: "100%", mb: 3 }}
    />
  );
}

function BranchSelectInput() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const { data: branches = [] } = useGetList<Branch>("branches", { filter: { drivingSchoolId } });
  return (
    <SelectInput
      source="branchId"
      label="Filiale *"
      options={branches.map((it) => [it.id, it.name])}
      validate={(value) => (value ? true : `Bitte wähle die Filiale aus.`)}
      size="small"
      sx={{ width: "100%", mb: 3 }}
    />
  );
}

function Bf17GridInput() {
  const isYoungerThan18Years = useIsYoungerThanXYears(18);
  if (!isYoungerThan18Years) return null;
  return (
    <Grid item xs={12} md={6}>
      <Column pt={3}>
        <FormLabel>
          Möchtest du am begleiteten Fahren teilnehmen? (Führerschein ab 17 / Führerscheinklasse BF17)
        </FormLabel>
        <YesNoInput source="assistedDrivingBF17" />
      </Column>
    </Grid>
  );
}

function LegalGuardianInputs() {
  const isYoungerThan18Years = useIsYoungerThanXYears(18);

  if (!isYoungerThan18Years) {
    return null;
  }

  return (
    <>
      <Grid item xs={12} md={12}>
        <Typography variant="body2" fontSize="14px" color="textSecondary">
          Gesetzlicher Vertreter
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput label="Vorname" source="guardian.firstName" validate={required()} autoFocus fullWidth />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput label="Nachname" source="guardian.lastName" validate={required()} autoFocus fullWidth />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput
          label="E-Mail"
          source="guardian.email"
          validate={(email) => {
            if (!email?.trim()) {
              return "Bitte gib deine E-Mail-Adresse ein.";
            }
            if (!_isEmail(email.trim())) {
              return "Bitte gib eine gültige E-Mail-Adresse ein.";
            }
            if (email.endsWith("@mustermann.de")) {
              return "Bitte gib eine andere E-Mail-Adresse ein.";
            }
            return null;
          }}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput label="Telefonnummer" source="guardian.phone" validate={required()} fullWidth />
      </Grid>
    </>
  );
}

function _splitStreet(street: string): [string, string] {
  const match = street.match(/^(.*?)(\d+.*)$/);
  if (!match) {
    return [street?.trim(), ""];
  }
  return [match[1].trim(), match[2].trim()];
}

function _isEmail(email: string): boolean {
  return (
    (String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      )?.length ?? 0) >= 1
  );
}

function _isYoungerThanXYears(birthday: DateTime, years: number): boolean {
  const age = DateTime.now().diff(birthday).as("years");
  return birthday.isValid && age < years;
}

function useIsYoungerThanXYears(years: number): boolean {
  const { watch } = useFormContext<CreateStudentFormValues>();
  const dateOfBirth = watch("dateOfBirth");
  if (!dateOfBirth) {
    return false;
  }
  const birthday = DateTime.fromISO(dateOfBirth);
  return _isYoungerThanXYears(birthday, years);
}
